export function arraysHaveCommonItem<T = string | number>(
  arr1: T[],
  arr2: T[],
): boolean {
  const set = new Set(arr1);
  return arr2.some((item) => set.has(item));
}

export function arraysHaveSameItems<T = string | number>(
  arr1: T[],
  arr2: T[],
): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }

  const sortedArr1 = arr1.slice().sort();
  const sortedArr2 = arr2.slice().sort();

  for (let i = 0; i < sortedArr1.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) {
      return false;
    }
  }
  return true;
}

export function serializeQueryString(obj: Record<string, any>): string {
  const filteredObj = Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined),
  );
  return new URLSearchParams(filteredObj).toString();
}

export function getQueryParamValue(param: string): string | undefined {
  if (typeof window !== 'undefined' && window.location.search) {
    const queryString = window.location.search.substring(1);
    const params = queryString.split('&');

    for (let i = 0; i < params.length; i++) {
      const pair = params[i].split('=');
      if (pair[0] === param) {
        return pair[1];
      }
    }
  }
  return undefined;
}

export function getPathname(urlOrPath: string): string {
  const trimmedUrlOrPath = urlOrPath.trim();

  if (trimmedUrlOrPath.startsWith('/')) {
    const queryParamIndex = trimmedUrlOrPath.indexOf('?');
    if (queryParamIndex !== -1) {
      return trimmedUrlOrPath.substring(0, queryParamIndex);
    }
    return trimmedUrlOrPath;
  }

  const pathMatch = trimmedUrlOrPath.match(/\/(.*)/);

  if (pathMatch && pathMatch[1]) {
    const queryParamIndex = pathMatch[1].indexOf('?');
    if (queryParamIndex !== -1) {
      return '/' + pathMatch[1].substring(0, queryParamIndex);
    }
    return '/' + pathMatch[1];
  } else {
    return '/';
  }
}

export function trimAllStrings(obj: any): any {
  if (typeof obj === 'string') {
    return obj.trim();
  }

  if (Array.isArray(obj)) {
    return obj.map(trimAllStrings);
  }

  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, trimAllStrings(value)]),
    );
  }

  return obj;
}

/**
 * This function takes in an object, traverses it, and replaces falsely values (0, "", and  null) with undefined.
 * If a nested object has all its field values  as falsely values, that object is set to undefined. This is used
 * to strip out incomplete field data from a HTTP payload.
 * @param obj The object with the falsey values
 * @returns The object without the falsey values
 */
export function removeAllFalselyValues(obj: any): any {
  if (typeof obj === 'string') {
    return obj.trim() || undefined;
  }

  if (typeof obj === 'number') {
    return obj !== 0 ? obj : undefined;
  }

  if (obj === null) {
    return undefined;
  }

  if (Array.isArray(obj)) {
    const filteredArray = obj.map((item: any) => removeAllFalselyValues(item));
    return filteredArray.every((item: any) => !!item) ? obj : undefined;
  }

  if (obj !== null && typeof obj === 'object') {
    const filteredObj: any = {};
    Object.entries(obj).forEach(([key, value]) => {
      const newValue = removeAllFalselyValues(value);
      if (newValue) {
        filteredObj[key] = newValue;
      }
    });
    return Object.keys(filteredObj).length ? filteredObj : undefined;
  }

  return obj || undefined;
}

export function dateToString(date: Date) {
  date.setHours((-1 * date.getTimezoneOffset()) / 60);
  return date.toISOString().split('T', 1)[0];
}
